import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_jci-luxury-portal/artifacts/914ad0rv_Artboard%201.png";

export const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const quickLinks = [
    { path: '/about', label: 'About Us' },
    { path: '/membership', label: 'Membership' },
    { path: '/amenities', label: 'Amenities' },
    { path: '/events', label: 'Events' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/csr', label: 'CSR & Impact' },
    { path: '/contact', label: 'Contact' },
  ];

  const logoUrl = settings?.logo_url || DEFAULT_LOGO;

  return (
    <footer data-testid="footer" className="bg-[#0A192F] border-t border-[rgba(212,175,55,0.1)] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div>
            <img
              src={logoUrl}
              alt="Club JCI Ltd"
              className="h-14 w-auto mb-6"
            />
            <p className="text-[#94A3B8] text-sm leading-relaxed">
              An exclusive sanctuary for entrepreneurs, leaders, and changemakers to connect, 
              collaborate, and create lasting impact.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[#94A3B8] hover:text-[#D4AF37] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-[#94A3B8] text-sm">
                  {settings?.address || 'House: 11A, Road: 113, Gulshan 02, Dhaka'}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
                <div className="text-[#94A3B8] text-sm">
                  <a href={`tel:${settings?.phone1 || '+8801675381845'}`} className="hover:text-[#D4AF37] transition-colors block">
                    {settings?.phone1 || '+8801675381845'}
                  </a>
                  <a href={`tel:${settings?.phone2 || '+8801342689009'}`} className="hover:text-[#D4AF37] transition-colors block">
                    {settings?.phone2 || '+8801342689009'}
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
                <div className="text-[#94A3B8] text-sm">
                  <a href={`mailto:${settings?.email1 || 'contact@clubjci.com'}`} className="hover:text-[#D4AF37] transition-colors block">
                    {settings?.email1 || 'contact@clubjci.com'}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h4 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-6">Join The Club</h4>
            <p className="text-[#94A3B8] text-sm mb-6">
              Experience the privilege of membership. Apply today.
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="btn-gold text-xs py-3 px-6 rounded-sm inline-block"
            >
              Inquire Now
            </Link>
          </div>
        </div>

        {/* Marble Divider */}
        <div className="marble-line my-10 opacity-30"></div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#94A3B8] text-xs">
            © {new Date().getFullYear()} Club JCI Ltd. All rights reserved.
          </p>
          <Link
            to="/admin/login"
            className="text-[#94A3B8] hover:text-[#D4AF37] text-xs transition-colors"
          >
            Admin Panel
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
